import React, { Component } from "react";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import {
  Container, Button, Form, Modal 
} from "react-bootstrap";
import FocusTrap from "focus-trap-react";
import PasswordField from "../common/PasswordField";
import http from "../../api/http-request";
import PasswordRequirements from "../PasswordRequirements";
import { isPasswordFormatValid, pageNotFound } from "../../utils/common";
import CloseIcon from "../../assets/images/close-icon.svg";

class ChangePasswordPopup extends Component {
  constructor(props) {
    super(props);
    this.currentPasswordInputRef = React.createRef();
    this.newPasswordInputRef = React.createRef();
    this.confirmPasswordInputRef = React.createRef();
    this.state = {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
      currentPasswordError: "",
      newPasswordError: "",
      confirmPasswordError: ""
    };
  }

  onPasswordSubmit = (e) => {
    e.preventDefault();
    const { currentPassword, newPassword, confirmPassword } = this.state;
    const hasCurrentPassword = this.isFormFieldValid(
      currentPassword,
      "currentPasswordError"
    );
    const hasNewPassword = this.isFormFieldValid(
      newPassword,
      "newPasswordError"
    );
    const hasConfirmPassword = this.isFormFieldValid(
      confirmPassword,
      "confirmPasswordError"
    );
    if (hasCurrentPassword) {
      this.currentPasswordInputRef.current.focus();
    } else if (hasNewPassword) {
      this.newPasswordInputRef.current.focus();
    } else if (hasConfirmPassword) {
      this.confirmPasswordInputRef.current.focus();
    } else if (!hasCurrentPassword && !hasNewPassword && !hasConfirmPassword) {
      const { userId, onChangePasswordHandler } = this.props;
      const dataStoreToken = localStorage.getItem("authToken");
      const bearer = 'Bearer ';
      
      http(
        {
          url: `managed/user/${userId}`,
          method: "PATCH",
          data: [{ operation: "add", field: "/password", value: newPassword }]
        },
        {          
          Authorization: `${bearer}${dataStoreToken}`
        },
        {
          "X-OpenIDM-Reauth-Password": currentPassword
        }
      ).then((res) => {
        if (res.status === 200 && res.data && res.data._id === userId) {
          onChangePasswordHandler("success");
        } else if (
          res.status === 403 &&
          res.data.message.includes("Reauthentication failed for")
        ) {
          this.setState({
            currentPasswordError: "common.errors.incorrectPassword"
          });
          this.currentPasswordInputRef.current.focus();
        } else if (res.status === 200 && res.data && typeof res.data === 'string') {
          pageNotFound(res);
        } else {
          onChangePasswordHandler("error");
        }
      });
    }
  };

  isFormFieldValid = (e, key) => {
    const value = e.target ? e.target.value.trim() : e;
    const { currentPassword, newPassword, confirmPassword } = this.state;

    let errorMsg = "";
    switch (key) {
      case "currentPasswordError":
        if (value.length <= 0) {
          errorMsg = "common.errors.noCurrentPasswordError";
        }
        break;
      case "newPasswordError":
        if (value.length <= 0) {
          errorMsg = "common.errors.noNewPasswordError";
        } else if (isPasswordFormatValid(value)) {
          errorMsg = "common.errors.invalidFormat";
        } else if (currentPassword === confirmPassword) {
          errorMsg = "common.errors.currentNewPasswordSameError";
        }
        break;
      case "confirmPasswordError":
        if (value.length <= 0) {
          errorMsg = "common.errors.noConfirmPasswordError";
        } else if (newPassword !== confirmPassword) {
          errorMsg = "common.errors.passwordsError";
        }
        break;
      default:
        errorMsg = "";
        break;
    }
    this.setState({
      [key]: errorMsg
    });
    return errorMsg;
  };

  render() {
    const { t, onClose } = this.props;
    const {
      currentPassword,
      newPassword,
      confirmPassword,
      currentPasswordError,
      newPasswordError,
      confirmPasswordError
    } = this.state;

    return (
      <Modal
        show
        animation={false}
        onEscapeKeyDown={() => onClose()}
        aria-labelledby="password-modal"
      >
        <FocusTrap>
          <div className="popup change-password">
            <Container fluid>
              <div className="popup__wrapper change-password__popup">
                <Form>
                  <div className="popup__wrapper-body change-password__popup-body">
                    <div className="popup__wrapper-body-header change-password__popup-body-header">
                      <h6 id="password-modal">{t("pages.changePasswordPopup.changePassword")}</h6>
                      <Button
                        className="popup__wrapper-body-header-close change-password__popup-body-header-close"
                        onClick={() => {
                          onClose();
                        }}
                        variant="light"
                        aria-label="Close"
                      >
                        <img src={CloseIcon} alt="" />
                      </Button>
                    </div>
                    <Form.Group controlId="currentPassword">
                      <Form.Label>
                        {t("pages.changePasswordPopup.currentPassword")}
                      </Form.Label>

                      <PasswordField
                        onChange={(value) => {
                          this.setState({
                            currentPassword: value
                          });
                        }}
                        onBlur={(e) => {
                          this.isFormFieldValid(e, "currentPasswordError");
                        }}
                        password={currentPassword}
                        className={
                          currentPasswordError ? "error-input" : undefined
                        }
                        autoFocus
                        ariaInvalid={!!currentPasswordError}
                        ariaDescribedby="current-password-error"
                        myRef={this.currentPasswordInputRef}
                      />
                    </Form.Group>
                    <p className="error-text" id="current-password-error">
                      {currentPasswordError && t(currentPasswordError)}
                    </p>
                    <Form.Group controlId="newPassword">
                      <Form.Label>
                        {t("pages.changePasswordPopup.newPassword")}
                      </Form.Label>
                      <PasswordField
                        onChange={(value) => {
                          this.setState({
                            newPassword: value
                          },
                          () => {
                            if (confirmPassword !== '') {
                              this.isFormFieldValid(
                                value,
                                "confirmPasswordError"
                              );
                            }
                          });
                        }}
                        onBlur={(e) => {
                          this.isFormFieldValid(e, "newPasswordError");
                        }}
                        password={newPassword}
                        className={newPasswordError ? "error-input" : undefined}
                        ariaInvalid={!!newPasswordError}
                        ariaDescribedby="new-password-error password-requirements"
                        myRef={this.newPasswordInputRef}
                      />
                    </Form.Group>
                    <p className="error-text" id="new-password-error">
                      {newPasswordError && t(newPasswordError)}
                    </p>
                    <Form.Group controlId="confirmPassword">
                      <Form.Label>
                        {t("pages.changePasswordPopup.confirmPassword")}
                      </Form.Label>
                      <PasswordField
                        onChange={(value) => {
                          this.setState(
                            {
                              confirmPassword: value
                            },
                            () => {
                              this.isFormFieldValid(
                                value,
                                "confirmPasswordError"
                              );
                            }
                          );
                        }}
                        onBlur={(e) => {
                          this.isFormFieldValid(e, "confirmPasswordError");
                        }}
                        password={confirmPassword}
                        className={
                          confirmPasswordError ? "error-input" : undefined
                        }
                        ariaInvalid={!!confirmPasswordError}
                        ariaDescribedby="confirm-password-error"
                        myRef={this.confirmPasswordInputRef}
                        onCopy={(e) => {
                          e.preventDefault();
                          e.nativeEvent.stopImmediatePropagation();
                        }}
                        onPaste={(e) => {
                          e.preventDefault();
                          e.nativeEvent.stopImmediatePropagation();
                        }}
                      />
                    </Form.Group>
                    <p className="error-text" id="confirm-password-error">
                      {confirmPasswordError && t(confirmPasswordError)}
                    </p>
                    <PasswordRequirements
                      password={newPassword}
                      formSubmitted={false}
                    />
                  </div>
                  <div className="popup__wrapper-footer change-password__popup-footer">
                    <Button
                      className="wrapper__container-card-body-submit"
                      size="lg"
                      variant="primary"
                      block
                      type="submit"
                      onClick={(e) => this.onPasswordSubmit(e)}
                    >
                      {t("pages.changePasswordPopup.changePassword")}
                    </Button>
                  </div>
                </Form>
              </div>
            </Container>
          </div>
        </FocusTrap>
      </Modal>
    );
  }
}

ChangePasswordPopup.propTypes = {
  t: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  userId: PropTypes.string.isRequired,
  onChangePasswordHandler: PropTypes.func.isRequired
};
export default withTranslation()(ChangePasswordPopup);
